/**
 * Entry application component used to compose providers and render Routes.
 * */
import React, { useEffect } from "react";
import { Provider, useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { Routes } from "../app/Routes";
import { I18nProvider } from "../_metronic/i18n";
import { LayoutSplashScreen, MaterialThemeProvider } from "../_metronic/layout";
import { library } from '@fortawesome/fontawesome-svg-core'
import { fab } from '@fortawesome/free-brands-svg-icons'
import { faSignOutAlt, faBars, faChartBar, faTachometerAlt, faMoneyBill, faUserAlt, faCar, faTaxi, faUserFriends, faKeyboard, faClock, faCompass, faTicketAlt, faCity, faMap, faCommentAlt, faFileAlt, faMapMarkerAlt, faCheckCircle, faTimesCircle, faDharmachakra, faWallet, faClipboardList, faBolt} from '@fortawesome/free-solid-svg-icons'
import { actions as authActions } from "./modules/Auth/_redux/authRedux";
import ErrorBoundary from "./components/ErrorBoundary";

library.add(fab, faSignOutAlt, faBars, faChartBar, faDharmachakra, faTachometerAlt, faMoneyBill, faUserAlt, faCar, faTaxi, faUserFriends, faKeyboard, faClock, faCompass, faTicketAlt, faCity, faMap, faCommentAlt, faFileAlt, faMapMarkerAlt, faCheckCircle, faTimesCircle, faWallet, faClipboardList, faBolt)

// Inner component that can use dispatch
function AppContent({ basename }) {
  const dispatch = useDispatch();

  useEffect(() => {
    // One-time cleanup of corrupted localStorage values
    // This fixes the "undefined" is not valid JSON error for users with old cache
    const cleanupLocalStorage = () => {
      const cleanupVersion = 'v1_localStorage_cleanup_done';
      
      // Only run cleanup once per browser
      if (!localStorage.getItem(cleanupVersion)) {
        console.log('Running one-time localStorage cleanup...');
        
        const keys = Object.keys(localStorage);
        let cleanedCount = 0;
        
        keys.forEach(key => {
          const value = localStorage.getItem(key);
          // Remove items with literal string "undefined" or "null"
          if (value === "undefined" || value === "null") {
            localStorage.removeItem(key);
            cleanedCount++;
            console.log(`Cleaned corrupted localStorage key: ${key}`);
          }
        });
        
        // Mark cleanup as done
        localStorage.setItem(cleanupVersion, 'true');
        console.log(`localStorage cleanup complete. Removed ${cleanedCount} corrupted items.`);
      }
    };
    
    // Run cleanup immediately
    cleanupLocalStorage();
    
    // Initialize authentication on app startup
    dispatch(authActions.initAuth());
    
    // Handle chunk loading errors (cache issues)
    const handleChunkError = (event) => {
      if (event.reason && event.reason.name === 'ChunkLoadError') {
        console.error('Chunk loading failed, likely due to cache issues:', event.reason);
        // Show user-friendly message and suggest refresh
        if (window.confirm('The application needs to be refreshed due to a cache issue. Would you like to refresh now?')) {
          window.location.reload();
        }
      }
    };
    
    window.addEventListener('unhandledrejection', handleChunkError);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleChunkError);
    };
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <React.Suspense fallback={<LayoutSplashScreen />}>
        {/* Override `basename` (e.g: `homepage` in `package.json`) */}
        <BrowserRouter basename={basename}>
          {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
          <MaterialThemeProvider>
            {/* Provide `react-intl` context synchronized with Redux state.  */}
            <I18nProvider>
              {/* Render routes with provided `Layout`. */}
              <Routes />
            </I18nProvider>
          </MaterialThemeProvider>
        </BrowserRouter>
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default function App({ store, persistor, basename }) {
  return (
    /* Provide Redux store */
    <Provider store={store}>
      {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
      <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
        <AppContent basename={basename} />
      </PersistGate>
    </Provider>
  );
}
